import { useState } from 'react';

import { Navigate } from '@tanstack/react-router';

import { useActiveOrg, usePermissions } from '@src/context/hooks';
import { Form, Submit, TextField, useFormContext } from '@src/corelib/forms/generic';
import { useBusinessSaveMutation, useMineQuery } from '@src/query';

import { Toaster, useToasts } from '../Listings/useToasts';
import { Switch } from './controls';
import PageHeader from './PageHeader';
import { storefrontDefaults, storefrontSchema, storefrontUrl } from './storefrontForm';


function StorefrontFields({ baseDomain }) {
  const { watch } = useFormContext();
  const slug = watch('slug');

  return (
    <>
      <TextField
        name="slug"
        label="Storefront address"
        trailing={<span className="text-table text-ink-subtle">{`.${baseDomain}`}</span>}
        placeholder="my-shop"
      />

      <p className="text-xs text-ink-subtle">Lowercase letters, numbers, and hyphens. Some names are reserved.</p>

      <div className="flex items-center gap-1.5 text-table">
        <span className="text-ink-subtle">Public URL</span>
        <span className="font-medium text-ink">{storefrontUrl(slug, baseDomain)}</span>
      </div>

      <div className="flex">
        <Submit className="h-[42px] px-[22px] text-sm">Save changes</Submit>
      </div>
    </>
  );
}

StorefrontFields.displayName = 'StorefrontFields';


/** Storefront page (M13): the org's public buyer-facing parts site — a unique
 * slug + enable toggle, PATCHed together. Owner/Manager only; other roles are
 * redirected. Slug rules (format/uniqueness/reserved) are server-driven. */
function Storefront() {
  const { toasts, push } = useToasts();
  const { queryKey: mineKey } = useMineQuery();
  const { seller } = useActiveOrg();
  const { canManageOrg: canManage } = usePermissions();
  const saveMutation = useBusinessSaveMutation();
  const [enabled, setEnabled] = useState(() => Boolean(seller?.storefront_enabled));

  if (!seller) {
    return null;
  }

  if (!canManage) {
    return <Navigate replace to="/app/settings" />;
  }

  const save = (values) => saveMutation.mutateAsync({
    sellerId: seller.id,
    payload: { slug: values.slug.trim().toLowerCase(), storefront_enabled: enabled },
    queryKey: mineKey,
  }).then(() => push('Storefront saved'));

  return (
    <div className="flex flex-1 flex-col gap-5 lg:p-7">
      <PageHeader title="Storefront" subtitle="Your public buyer-facing parts site" />

      <div className="flex w-full max-w-[640px] flex-col gap-4 px-4 pb-6 lg:px-0">
        <div className="flex items-center gap-3.5 rounded-lg border border-hairline bg-surface-1 px-4 py-3.5">
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-sm font-semibold text-ink">Enable storefront</span>
            <span className="text-xs text-ink-subtle">Show your available parts at a public address.</span>
          </span>

          <Switch
            checked={enabled}
            disabled={saveMutation.isPending}
            label={`${enabled ? 'Disable' : 'Enable'} storefront`}
            onChange={() => setEnabled((v) => !v)}
          />
        </div>

        <p className="text-xs text-ink-subtle">Enabling your storefront requires a slug. Save to apply.</p>

        <Form
          schema={storefrontSchema}
          className="flex flex-col gap-4"
          defaultValues={storefrontDefaults(seller)}
          onSubmit={save}
          onError={(error, meta) => {
            if (!meta?.isInvalid) {
              push('Could not save storefront', 'danger');
            }
          }}
        >
          <StorefrontFields baseDomain={seller.storefront_base_domain} />
        </Form>
      </div>

      <Toaster toasts={toasts} />
    </div>
  );
}

Storefront.displayName = 'Storefront';
export default Storefront;
